import { isNodeDetached } from './common';
import { merge } from '../../loader/util';

var noop = function () {};

function getMutationHandler(mutation, handlers) {
    if (mutation.type === 'childList' && typeof handlers.childList === 'function') {
        return handlers.childList;
    } else if (mutation.type === 'characterData' && typeof handlers.characterData === 'function') {
        return handlers.characterData;
    } else if (mutation.type === 'attributes' && typeof handlers[mutation.attributeName] === 'function') {
        return handlers[mutation.attributeName];
    }

    // In IE11 there is some bug with attributeFilter.
    // Attribute mutation record is emitted for an attribute which is not in the list
    return noop;
}

function updateObserverOptions(options, attribute) {
    if (attribute === 'childList') {
        options.childList = true;
    } else if (attribute === 'characterData') {
        options.characterData = true;
    } else {
        if (options.attributes) {
            options.attributeFilter.push(attribute);
        } else {
            options.attributes = true;
            options.attributeFilter = [attribute];
        }
    }
}

function getMutationRecordLikeFromEvent(event) {
    var mutationRecordLike;

    switch (event.type) {
        case 'DOMAttrModified':
            mutationRecordLike = {
                type: 'attributes',
                target: event.target,
                attributeName: event.attrName,
                oldValue: event.prevValue
            };
            break;
        case 'DOMSubtreeModified':
            mutationRecordLike = {
                type: 'childList',
                target: event.target
            };
            break;
        case 'DOMCharacterDataModified':
            mutationRecordLike = {
                type: 'characterData',
                target: event.target,
                oldValue: event.prevValue
            };
            break;
        default:
            mutationRecordLike = {};
    }

    return mutationRecordLike;
}

function setPropertyCallbacks(target, callbacks) {
    var targetPrototype = Object.getPrototypeOf(target),
        propertiesToWatch = Object.keys(callbacks);

    propertiesToWatch.forEach(function (propertyName) {
        var propDescriptor = Object.getOwnPropertyDescriptor(targetPrototype, propertyName),
            newPropDescriptor;

        if (propDescriptor) {
            newPropDescriptor = merge({}, propDescriptor);
            newPropDescriptor.set = function (newValue) {
                var oldValue = propDescriptor.get.call(target);
                if (oldValue !== newValue) {
                    propDescriptor.set.call(target, newValue);
                    callbacks[propertyName].call(target, newValue);
                }
            };
            Object.defineProperty(target, propertyName, newPropDescriptor);
        }
    });
}

function createObserverCallback(handlers) {
    return function (mutations) {
        mutations.forEach(function (mutation) {
            getMutationHandler(mutation, handlers)(mutation);
        });
    };
}

function prepareOptions(target, optionGroups, handlers) {
    var attribute,
        optionName,
        options = {},
        supportedOptions = ['subtree', 'attributeOldValue', 'characterDataOldValue'],
        i;

    for (i = 0; i < optionGroups.length && (optionName = optionGroups[i]); i++) {
        options[optionName] = {};

        if (optionName === 'properties') {
            setPropertyCallbacks(target, handlers[optionName]);
            continue;
        }

        for (attribute in handlers[optionName]) {
            if (handlers[optionName].hasOwnProperty(attribute)) {
                updateObserverOptions(options[optionName], attribute);
            }
        }

        options[optionName][optionName] = supportedOptions.indexOf(optionName) > -1;
    }

    return options;
}

/**
 * Add DOM change handlers to the target.
 *
 * @param {HTMLElement} target - The HTML node which DOM changes are observed.
 * @param {Object} handlers - An object that groups handler definitions by different Observer options
 * @example
 * var mutationObserver = domchange.addMutationObservers(element, {
 *     node: {
 *         'data-small': function (mutation) {
 *             // Will be called if data-small attribute is changed on target element
 *         },
 *         'data-size': function...
 *     },
 *     properties: {
 *         'value': function () {
 *             // Will be called if value property is changed on target element
 *         }
 *     },
 *     subtree: {
 *        'data-big': function (mutation) {
 *            // Will be called if data-big attribute is changed on any descendant element of target
 *        },
 *        childList: function (mutation) {
 *            // Will be called if any descendant element is added or removed of target. Including text nodes
 *        },
 *        characterData: function (mutation) {
 *            // Will be called if any descendant text node value is changed
 *        }
 *     }
 * });
 *
 * mutationObserver.byPass(function () {
 *     // Mutation observer is disabled here. You can make any DOM mutations without triggering handlers
 *     // on this observer instance
 * });
 *
 * // Shut down mutation observing
 * mutationObserver.disconnect();
 */
function addMutationObservers(target, handlers) {
    var connect,
        disconnect,
        observers = {},
        optionGroups = Object.keys(handlers),
        options = prepareOptions(target, optionGroups, handlers),
        optionName,
        i;

    for (i = 0; i < optionGroups.length && (optionName = optionGroups[i]); i++) {
        if (optionName !== 'properties') {
            observers[optionName] = new window.MutationObserver(createObserverCallback(handlers[optionName]));
        }
    }

    connect = function () {
        for (var optionName in observers) {
            if (observers.hasOwnProperty(optionName)) {
                observers[optionName].observe(target, options[optionName]);
            }
        }
    };
    disconnect = function () {
        for (var optionName in observers) {
            if (observers.hasOwnProperty(optionName)) {
                observers[optionName].disconnect();
            }
        }
    };

    connect();

    return {
        byPass: function (fn) {
            var result;
            disconnect();
            result = fn();
            connect();
            return result;
        },
        disconnect: disconnect
    };
}

function addMutationEvents(target, handlers) {
    var connect,
        disconnect,
        optionGroups = Object.keys(handlers),
        options = prepareOptions(target, optionGroups, handlers),
        disconnectListeners = {};

    connect = function () {
        for (var optionName in options) {
            if (options.hasOwnProperty(optionName)) {
                disconnectListeners[optionName] = addEventListeners(target, options[optionName], handlers[optionName]);
            }
        }
    };

    disconnect = function () {
        for (var optionName in disconnectListeners) {
            if (disconnectListeners.hasOwnProperty(optionName)) {
                disconnectListeners[optionName].disconnect();
            }
        }
    };

    connect();

    return {
        byPass: function (fn) {
            var result;
            disconnect();
            result = fn();
            connect();
            return result;
        },
        disconnect: disconnect
    };
}

function shouldHandleMutation(options, mutation) {
    var inOptions = mutation.type === 'childList'
            || mutation.type === 'characterData'
            || mutation.attributeName
            && options.attributeFilter.length
            && options.attributeFilter.indexOf(mutation.attributeName) > -1,
        // should a change on a child be handled
        isSubtreeOrTarget = options.subtree || mutation.target === options.target,
        // is it a DOMSubtreeModified event following a DOMAttrModified event for the same node
        subtreeAfterAttr = mutation.type === 'childList' && (options.lastAttrTarget === mutation.target);

    // workaround for DOMSubtreeModified event following a DOMAttrModified event for the same node
    if (inOptions && mutation.type === 'attributes') {
        options.lastAttrTarget = mutation.target;
    } else if (subtreeAfterAttr) {
        options.lastAttrTarget = null;
    }

    return inOptions && isSubtreeOrTarget && !subtreeAfterAttr && !isNodeDetached(mutation.target);
}

function getEventHandler(options) {
    return function (event) {
        var mutation = getMutationRecordLikeFromEvent(event),
            handler = getMutationHandler(mutation, options.handlers);

        // should handle constraints implicitly existing in mutation observers
        if (shouldHandleMutation(options, mutation)) {
            handler(mutation);
        }
    };
}

function getEventNameOfType(type) {
    var eventListenersForTypes = {
        childList: 'DOMSubtreeModified',
        characterData: 'DOMCharacterDataModified',
        attributes: 'DOMAttrModified'
    };
    return eventListenersForTypes[type];
}

function getListenersDisconnectFunction(target, listeners) {
    return function disconnectAllListeners() {
        for (var eventType in listeners) {
            if (listeners.hasOwnProperty(eventType)) {
                target.removeEventListener(eventType, listeners[eventType], true);
            }
        }
    };
}

function addEventListeners(target, optionGroup, allHandlers) {
    var eventHandler,
        disconnectListeners = {},
        optionKeys,
        eventType,
        eventName,
        i;

    optionKeys = Object.keys(optionGroup);

    for (i = 0; i < optionKeys.length; i++) {
        eventType = optionKeys[i];

        optionGroup.target = target;
        optionGroup.handlers = allHandlers;
        eventHandler = getEventHandler(optionGroup);
        eventName = getEventNameOfType(eventType);
        target.addEventListener(eventName, eventHandler, true);
        disconnectListeners[eventName] = eventHandler;
    }

    return {
        disconnect: getListenersDisconnectFunction(target, disconnectListeners)
    };
}

export function addMutationHandlers(target, handlers) {
    if (window.MutationObserver) {
        return addMutationObservers(target, handlers);
    }
    if (window.addEventListener) {
        return addMutationEvents(target, handlers);
    }
}



//////////////////
// WEBPACK FOOTER
// ./shared/modules/domchange.js
// module id = 5
// module chunks = main